'use client';

import { DescriptionTooltip } from '@/components/form/dynamic-field/components/description-tooltip';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import type { Invoice } from '@pigello/pigello-matrix';
import { DateTime } from 'luxon';

export function getInvoiceStatus(invoice: Invoice): {
  text: string;
  badgeVariant: BadgeVariant['variant'];
  additionalInfo?: string;
} {
  const totalInvoiceValue = invoice.invoiceValuesMeta?.total_value ?? 0;
  const now = DateTime.now();

  if (totalInvoiceValue < 0) {
    return { text: 'Krediterad', badgeVariant: 'outline' };
  }

  if (invoice.closedTime) {
    if (
      invoice.debtInvoiceCounts?.unclosed_debt_invoices &&
      invoice.debtInvoiceCounts?.unclosed_debt_invoices > 0
    ) {
      return {
        text: 'Slutbetald (Öppna påminnelser/krav)',
        badgeVariant: 'orange',
      };
    }

    return { text: 'Slutbetald', badgeVariant: 'green' };
  }

  // has actually sent debt
  if (
    invoice.debtInvoiceCounts?.kinds?.['1'] !== undefined &&
    invoice.debtInvoiceCounts?.kinds?.['1'] > 0
  ) {
    return invoice.automaticDebtInvoicingPaused
      ? { text: 'Kravhantering (Pausad)', badgeVariant: 'light-gray' }
      : { text: 'Kravhantering', badgeVariant: 'purple' };
  }

  // reminders

  // has actually sent reminders
  if (
    invoice.debtInvoiceCounts?.kinds?.['0'] !== undefined &&
    invoice.debtInvoiceCounts?.kinds?.['0'] > 0
  ) {
    return invoice.automaticDebtInvoicingPaused
      ? { text: 'Påminnelse skickad (Pausad)', badgeVariant: 'light-gray' }
      : { text: 'Påminnelse skickad', badgeVariant: 'purple' };
  } else if (
    invoice.reminderAmount &&
    invoice.reminderAmount > 0 &&
    invoice.attestedDate &&
    now > DateTime.fromISO(invoice.dueDate).plus({ days: 1 })
  ) {
    return invoice.automaticDebtInvoicingPaused
      ? { text: 'Påminnelser pausade', badgeVariant: 'gray' }
      : { text: 'Påminnelse kommer skickas', badgeVariant: 'purple' };
  }

  // no reminders but overdue
  if (
    now > DateTime.fromISO(invoice.dueDate).plus({ days: 1 }) &&
    !invoice.closedTime &&
    invoice.attestedDate
  ) {
    return { text: 'Förfallen', badgeVariant: 'destructive' };
  }

  if (invoice.distributionDate && invoice.attestedDate && !invoice.closedTime) {
    if (invoice.paymentsSum && invoice.paymentsSum > 0) {
      return { text: 'Skickad, delbetald', badgeVariant: 'cyan' };
    }
    return { text: 'Skickad', badgeVariant: 'blue' };
  }

  if (!invoice.attestedDate) {
    return { text: 'Ej attesterad', badgeVariant: 'yellow' };
  }

  if (invoice.invoiceNumber && invoice.ocrNumber) {
    return { text: 'Attesterad', badgeVariant: 'green' };
  }

  return {
    text: 'Misslyckad attestering',
    badgeVariant: 'destructive',
    additionalInfo:
      'Attesteringen av denna avi/faktura misslyckades. Försök igen.',
  };
}

export function InvoiceStatus({ invoice }: { invoice: Invoice }) {
  const status = getInvoiceStatus(invoice);

  if (status.additionalInfo) {
    return (
      <Badge variant={status.badgeVariant}>
        {status.text}{' '}
        <DescriptionTooltip
          className='max-w-[200px] text-xs font-normal whitespace-normal'
          description={status.additionalInfo}
        />
      </Badge>
    );
  }

  return <Badge variant={status.badgeVariant}>{status.text}</Badge>;
}
